import React, { useCallback } from 'react';
import { batchActions } from 'redux-batched-actions';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { FIELD_TYPE_MAP } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/constants.tsx';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import {
	STORY_POINT_ESTIMATE_CF_TYPE,
	TIME_ESTIMATE_TYPE,
	TIME_TRACKING_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	type MenuNode,
	MenuNodeType,
} from '@atlassian/jira-software-context-menu/src/common/types.tsx';
import { useContextStartAnalytics } from '../../../../../../common/hooks/use-context-start-analytics/index.tsx';
import { ORIGINAL_ESTIMATE, STORY_POINTS } from '../../../../../../model/constants.tsx';
import { cardEstimateSetOptimistic } from '../../../../../../state/actions/estimate/index.tsx';
import { useBacklogActionCreator, useBacklogSelector } from '../../../../../../state/index.tsx';
import { getTimeTrackingOptions } from '../../../../../../state/selectors/app/index.tsx';
import { makeCardKeyFromCardId } from '../../../../../../state/selectors/cards/index.tsx';
import {
	getEstimationFieldId,
	getEstimationType,
} from '../../../../../../state/selectors/estimation/index.tsx';
import {
	canBulkEditIssues,
	canEditIssue,
} from '../../../../../../state/selectors/permissions/index.tsx';
import {
	STORY_POINTS_MENU_ID,
	STORY_POINTS_EDIT_MENU_ID,
	ORIGINAL_ESTIMATE_MENU_ID,
	ORIGINAL_ESTIMATE_EDIT_MENU_ID,
} from '../../constants.tsx';
import messages from '../../messages.tsx';
import { AsyncOriginalEstimateField } from './original-estimate-field/async.tsx';
import { AsyncStoryPointField } from './story-point-field/async.tsx';
import type {
	OriginalEstimateSubmitHandlerArgs,
	CancelHandlerArgs,
	SubmitHandlerArgs,
	StoryPointContentProps,
	OriginalEstimateContentProps,
	OriginalEstimateValue,
	Props,
} from './types.tsx';

export const useStoryPointContent = ({
	onSubmit,
	onCancel,
	selectedCardIds,
}: StoryPointContentProps): MenuNode[] => {
	const { formatMessage } = useIntl();

	const createConfirmHandler = useCallback(
		(scope: string, closeMenu: () => void, analyticsEvent: UIAnalyticsEvent) =>
			(value: number | null) =>
				onSubmit({ scope, value, closeMenu, analyticsEvent }),
		[onSubmit],
	);

	const createCancelHandler = useCallback(
		(scope: string, closeMenu: () => void, analyticsEvent: UIAnalyticsEvent) => () =>
			onCancel({ scope, closeMenu, analyticsEvent }),
		[onCancel],
	);

	const onMouseEnter = useContextStartAnalytics({
		selectedCardIds: selectedCardIds ?? [],
		fieldType: FIELD_TYPE_MAP[STORY_POINT_ESTIMATE_CF_TYPE],
		fieldKey: FIELD_TYPE_MAP[STORY_POINT_ESTIMATE_CF_TYPE],
	});

	const storyPointNode: MenuNode = {
		type: MenuNodeType.Node,
		id: STORY_POINTS_EDIT_MENU_ID,
		node: ({ scope, closeMenu, analyticsEvent }) => (
			<AsyncStoryPointField
				onConfirm={createConfirmHandler(scope, closeMenu, analyticsEvent)}
				onCancel={createCancelHandler(scope, closeMenu, analyticsEvent)}
			/>
		),
	};

	const children = fg('jfp_a11y_team_boards_list_markup_used_incorrectly')
		? storyPointNode
		: [storyPointNode];

	return [
		{
			type: MenuNodeType.Item,
			id: STORY_POINTS_MENU_ID,
			label: formatMessage(messages.storyPointEstimate),
			shouldOverflowChildren: true,
			submenuWidth: 86,
			children,
			...(fg('one_event_rules_them_all_fg') && {
				onMouseEnter,
			}),
		},
	];
};

export const useOriginalEstimateContent = ({
	onSubmit,
	onCancel,
	timeTrackingOptions,
	selectedCardIds,
}: OriginalEstimateContentProps): MenuNode[] => {
	const { formatMessage } = useIntl();

	const createConfirmHandler = useCallback(
		(scope: string, closeMenu: () => void, analyticsEvent: UIAnalyticsEvent) =>
			(estimate: OriginalEstimateValue) =>
				onSubmit({ scope, estimate, closeMenu, analyticsEvent }),
		[onSubmit],
	);

	const createCancelHandler = useCallback(
		(scope: string, closeMenu: () => void, analyticsEvent: UIAnalyticsEvent) => () =>
			onCancel({ scope, closeMenu, analyticsEvent }),
		[onCancel],
	);

	const onMouseEnter = useContextStartAnalytics({
		selectedCardIds: selectedCardIds ?? [],
		fieldType: TIME_ESTIMATE_TYPE,
		fieldKey: TIME_ESTIMATE_TYPE,
	});

	if (!timeTrackingOptions) {
		return [];
	}

	return [
		{
			type: MenuNodeType.Item,
			id: ORIGINAL_ESTIMATE_MENU_ID,
			label: formatMessage(messages.originalEstimate),
			shouldOverflowChildren: true,
			submenuWidth: 126,
			children: [
				{
					type: MenuNodeType.Node,
					id: ORIGINAL_ESTIMATE_EDIT_MENU_ID,
					node: ({ scope, closeMenu, analyticsEvent }) => (
						<AsyncOriginalEstimateField
							onConfirm={createConfirmHandler(scope, closeMenu, analyticsEvent)}
							onCancel={createCancelHandler(scope, closeMenu, analyticsEvent)}
							timeTrackingOptions={timeTrackingOptions}
						/>
					),
				},
			],
			...(fg('one_event_rules_them_all_fg') && {
				onMouseEnter,
			}),
		},
	];
};

export const useEstimate = ({ selectedCardIds }: Props): MenuNode[] => {
	const estimationType = useBacklogSelector((state) => getEstimationType(state));
	const timeTrackingOptions = useBacklogSelector((state) => getTimeTrackingOptions(state));
	const estimationFieldId = useBacklogSelector(getEstimationFieldId);
	const getIssueKey = useBacklogSelector(makeCardKeyFromCardId);

	const canBulkEdit = useBacklogSelector(canBulkEditIssues);
	const canEdit = useBacklogSelector(canEditIssue);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleCancel = useCallback(({ closeMenu }: CancelHandlerArgs) => {
		closeMenu();
	}, []);

	const handleSubmitStoryPoint = useBacklogActionCreator(
		({ value, closeMenu, analyticsEvent }: SubmitHandlerArgs) => {
			closeMenu();
			if (selectedCardIds.length === 1 && fg('one_event_rules_them_all_fg')) {
				getUpdateAnalyticsFlowHelper().fireAnalyticsEnd(
					FIELD_TYPE_MAP[STORY_POINT_ESTIMATE_CF_TYPE],
					{
						analytics: createAnalyticsEvent({}),
						attributes: {
							fieldType: FIELD_TYPE_MAP[STORY_POINT_ESTIMATE_CF_TYPE],
						},
					},
				);
			}

			const actions = selectedCardIds.map((id) =>
				cardEstimateSetOptimistic(
					{
						cardId: id,
						estimate: { storyPoints: value },
					},
					{
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						estimationFieldId: estimationFieldId!,
						issueKey: getIssueKey(id),
					},
					analyticsEvent,
				),
			);

			return batchActions(actions);
		},
	);

	const handleSubmitOriginalEstimate = useBacklogActionCreator(
		({ estimate, closeMenu, analyticsEvent }: OriginalEstimateSubmitHandlerArgs) => {
			closeMenu();
			if (selectedCardIds.length === 1 && fg('one_event_rules_them_all_fg')) {
				getUpdateAnalyticsFlowHelper().fireAnalyticsEnd(TIME_ESTIMATE_TYPE, {
					analytics: createAnalyticsEvent({}),
					attributes: {
						fieldType: TIME_ESTIMATE_TYPE,
					},
				});
			}

			const actions = selectedCardIds.map((id) =>
				cardEstimateSetOptimistic(
					{
						cardId: id,
						estimate: {
							originalEstimate: {
								value: estimate.value || 0,
								valueAsText: estimate.valueAsText,
							},
						},
					},
					{
						estimationFieldId: TIME_TRACKING_TYPE,
						issueKey: getIssueKey(id),
					},
					analyticsEvent,
				),
			);

			return batchActions(actions);
		},
	);

	const storyPointContent = useStoryPointContent({
		onSubmit: handleSubmitStoryPoint,
		onCancel: handleCancel,
		...(fg('one_event_rules_them_all_fg') && { selectedCardIds }),
	});

	const originalEstimateContent = useOriginalEstimateContent({
		onSubmit: handleSubmitOriginalEstimate,
		onCancel: handleCancel,
		timeTrackingOptions,
		...(fg('one_event_rules_them_all_fg') && { selectedCardIds }),
	});

	const canEditEstimate = canBulkEdit && canEdit && !!estimationFieldId;

	if (canEditEstimate && estimationType === STORY_POINTS) {
		return storyPointContent;
	}

	if (canEditEstimate && estimationType === ORIGINAL_ESTIMATE) {
		return originalEstimateContent;
	}

	return [];
};
